package com.sddamico.draftmachine.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.roleMono
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span

/** Semantic tone of a badge — a solid neutral chip or a translucent metal-tinted chip. */
enum class BadgeTone { Neutral, Brass, Rust, Verde, Glass, Patina }

/** Per-tone fill / foreground / border, plus the fg hex used for the optional dot's glow. */
private class BadgeToneSpec(val bg: Color, val fg: Color, val bd: Color, val fgHex: String)

private fun toneSpec(tone: BadgeTone): BadgeToneSpec = when (tone) {
  BadgeTone.Neutral -> BadgeToneSpec(DmColor.steel800, DmColor.textSecondary, DmColor.borderStrong, "#B0BFCC")
  BadgeTone.Brass -> BadgeToneSpec(DmColor.brassTint(0.16f), DmColor.brass300, DmColor.brass600, "#E6B183")
  BadgeTone.Rust -> BadgeToneSpec(Color.rgba(180, 81, 42, 0.18f), DmColor.rust300, DmColor.rust600, "#E28A66")
  BadgeTone.Verde -> BadgeToneSpec(Color.rgba(74, 130, 112, 0.18f), DmColor.verde300, DmColor.verde600, "#86C7AB")
  BadgeTone.Glass -> BadgeToneSpec(Color.rgba(47, 166, 168, 0.18f), DmColor.glass300, DmColor.glass600, "#86DCD6")
  BadgeTone.Patina -> BadgeToneSpec(Color.rgba(78, 120, 130, 0.18f), DmColor.patina300, DmColor.patina600, "#94B6C6")
}

/**
 * A stamped uppercase status chip — mono micro-label with an optional glowing dot / icon.
 * Ports `components/core/Badge.jsx`.
 *
 * @param dot prepends a small glowing indicator in the foreground color.
 * @param icon bare Phosphor glyph name.
 * @param content optional slot; otherwise [label] is rendered.
 */
@Composable
fun Badge(
  label: String? = null,
  modifier: Modifier = Modifier,
  tone: BadgeTone = BadgeTone.Neutral,
  icon: String? = null,
  dot: Boolean = false,
  content: (@Composable () -> Unit)? = null,
) {
  val t = toneSpec(tone)
  val mod = Modifier
    .displayInlineFlex()
    .alignItems(AlignItems.Center)
    .gap(DmSpacing.space2)
    .padding(topBottom = 2.px, leftRight = DmSpacing.space3)
    .height(24.px)
    .backgroundColor(t.bg)
    .color(t.fg)
    .border(1.px, LineStyle.Solid, t.bd)
    .borderRadius(DmEffects.radiusPill)
    .roleMono()
    .fontSize(DmType.text2xs)
    .letterSpacing(DmType.trackingWide)
    .textTransform(TextTransform.Uppercase)
    .fontWeight(DmType.weightBold)
    .whiteSpace(WhiteSpace.NoWrap)
    .then(modifier)

  Span(attrs = mod.toAttrs()) {
    if (dot) {
      Span(
        attrs = Modifier
          .width(6.px)
          .height(6.px)
          .borderRadius(50.percent)
          .backgroundColor(t.fg)
          .boxShadowRaw("0 0 6px ${t.fgHex}")
          .toAttrs { attr("aria-hidden", "true") }
      ) {}
    }
    if (icon != null) {
      Icon(icon, IconWeight.Bold, Modifier.fontSize(12.px))
    }
    if (content != null) content() else if (label != null) SpanText(label)
  }
}
